import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {VectorMathNodeOperation} from "#material/nodes/converter/vector-math/constants"
import type {VectorMathSockets} from "#material/nodes/converter/vector-math/definition"
import {vectorMathDefinition} from "#material/nodes/converter/vector-math/definition"
import {cyclesOperation, moduloVector, projectedVector, snappedVector, wrappedVector} from "#material/nodes/converter/vector-math/helpers"
import type {CyclesReturns} from "#material/sockets/cycles"
import {threeConvert, threeValue, threeVec3} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {ThreeTsl as TSL} from "#material/three/exports"
import type {ThreeReturns} from "#material/sockets/three"
import {cyclesVec3} from "#material/cycles/helpers"
import type {VectorMathNode} from "#material/cycles/types"

@registerMaterialGraphNode
export class VectorMath extends MaterialNode<VectorMathSockets> {
    override get definition() {
        return vectorMathDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<VectorMathSockets>> => {
        const {
            vector: valueA,
            vector_001: valueB,
            vector_002: valueC,
        } = await evaluator.getAll({
            vector: this.parameters.inputs.vector,
            vector_001: this.parameters.inputs.vector_001,
            vector_002: this.parameters.inputs.vector_002,
        })
        const inputA =
            valueA?.toVec3() ??
            (typeof this.parameters.settings.vector === "number"
                ? threeVec3({x: this.parameters.settings.vector, y: this.parameters.settings.vector, z: this.parameters.settings.vector})
                : threeConvert(this.parameters.settings.vector, threeVec3)) ??
            threeVec3({x: 0, y: 0, z: 0})

        const inputB = valueB?.toVec3() ?? threeConvert(this.parameters.settings.vector_001, threeVec3) ?? threeVec3({x: 0, y: 0, z: 0})
        const inputC = valueC?.toVec3() ?? threeConvert(this.parameters.settings.vector_002, threeVec3) ?? threeVec3({x: 0, y: 0, z: 0})
        const scale = threeConvert(this.parameters.settings.scale, threeValue) ?? threeValue(1)
        const {useClamp} = this.parameters.settings
        const operation = this.parameters.settings.operation ?? "ADD"

        const getValue = () => {
            if (operation === "ADD") {
                return {vector: TSL.add(inputA, inputB)}
            } else if (operation === "SUBTRACT") {
                return {vector: TSL.sub(inputA, inputB)}
            } else if (operation === "MULTIPLY") {
                return {vector: TSL.mul(inputA, inputB)}
            } else if (operation === "DIVIDE") {
                return {vector: TSL.div(inputA, inputB)}
            } else if (operation === "DISTANCE") {
                return {value: TSL.distance(inputA, inputB)}
            } else if (operation === "LENGTH") {
                return {value: TSL.length(inputA)}
            } else if (operation === "NORMALIZE") {
                return {vector: TSL.normalize(inputA)}
            } else if (operation === "SCALE") {
                return {vector: TSL.mul(inputA, scale)}
            } else if (operation === "CROSS_PRODUCT") {
                return {vector: TSL.cross(inputA, inputB)}
            } else if (operation === "PROJECT") {
                return {vector: projectedVector(inputA, inputB)}
            } else if (operation === "REFLECT") {
                return {vector: TSL.reflect(inputA, TSL.normalize(inputB))}
            } else if (operation === "REFRACT") {
                return {vector: TSL.refract(inputA, TSL.normalize(inputB), scale)}
            } else if (operation === "FACEFORWARD") {
                return {vector: TSL.faceForward(inputA, inputB, inputC)}
            } else if (operation === "MULTIPLY_ADD") {
                return {vector: TSL.add(TSL.mul(inputA, inputB), inputC)}
            } else if (operation === "DOT_PRODUCT") {
                return {value: TSL.dot(inputA, inputB)}
            } else if (operation === "SNAP") {
                return {vector: snappedVector(inputA, inputB)}
            } else if (operation === "FLOOR") {
                return {vector: TSL.floor(inputA)}
            } else if (operation === "CEIL") {
                return {vector: TSL.ceil(inputA)}
            } else if (operation === "MODULO") {
                return {vector: moduloVector(inputA, inputB)}
            } else if (operation === "WRAP") {
                return {vector: wrappedVector(inputA, inputB, inputC)}
            } else if (operation === "FRACTION") {
                return {vector: TSL.fract(inputA)}
            } else if (operation === "ABSOLUTE") {
                return {vector: TSL.abs(inputA)}
            } else if (operation === "MINIMUM") {
                return {vector: TSL.min(inputA, inputB)}
            } else if (operation === "MAXIMUM") {
                return {vector: TSL.max(inputA, inputB)}
            } else if (operation === "SINE") {
                return {vector: TSL.sin(inputA)}
            } else if (operation === "COSINE") {
                return {vector: TSL.cos(inputA)}
            } else if (operation === "TANGENT") {
                return {vector: TSL.tan(inputA)}
            } else {
                throw new Error(`Invalid operation: ${operation}`)
            }
        }

        const {value, vector} = getValue()
        return {
            vector: useClamp && vector ? TSL.clamp(vector, threeValue(0), threeValue(1)) : (vector ?? threeVec3({x: 0, y: 0, z: 0})),
            value: useClamp && value ? TSL.clamp(value, threeValue(0), threeValue(1)) : (value ?? threeValue(0)),
        }
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<VectorMathSockets>> => {
        const operation = this.parameters.settings.operation as VectorMathNodeOperation

        const vectorMathNode: VectorMathNode = {
            type: "vector_math",
            inputs: await evaluator.getAll({
                vector1: this.parameters.inputs.vector,
                vector2: this.parameters.inputs.vector_001,
                vector3: this.parameters.inputs.vector_002,
                scale: undefined,
            }),
            parameters: {
                math_type: cyclesOperation(operation),
                vector1: cyclesVec3(this.parameters.settings.vector),
                vector2: cyclesVec3(this.parameters.settings.vector_001),
                vector3: cyclesVec3(this.parameters.settings.vector_002),
                scale: this.parameters.settings.scale,
            },
            resources: {},
        }

        return {vector: [vectorMathNode, "vector"], value: [vectorMathNode, "value"]}
    }
}
